import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { API } from "../App";
import {
  Siren, Loader2, ArrowLeft, BookOpen, Shield,
  AlertTriangle, FileText, Clock, BarChart3, Target,
  Activity, ListChecks, MessageSquare, ChevronRight,
  Zap, Eye, ExternalLink, Search, ThumbsUp, ThumbsDown
} from "lucide-react";

const RISK_COLORS = { low: "text-emerald-400", medium: "text-amber-400", high: "text-orange-400", critical: "text-red-400" };
const FINDING_COLORS = {
  observation: "text-blue-400", ofi: "text-cyan-400",
  minor_nonconformance: "text-amber-400", major_nonconformance: "text-orange-400",
  critical_nonconformance: "text-red-400",
};
const POSTURE_COLORS = {
  neutral: "text-slate-400", satisfied: "text-emerald-400", concern: "text-amber-400",
  escalating: "text-orange-400", finding_likely: "text-red-400",
};

const WarRoomPage = () => {
  const { t } = useTranslation('audit');
  const navigate = useNavigate();
  const [mode, setMode] = useState("intelligence");
  const [intel, setIntel] = useState(null);
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [briefing, setBriefing] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [history, setHistory] = useState([]);

  const fetchIntelligence = useCallback(async () => {
    try {
      const res = await fetch(`${API}/warroom/intelligence`, { credentials: "include" });
      if (res.ok) setIntel(await res.json());
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => {
    Promise.all([
      fetch(`${API}/warroom/topics`).then(r => r.json()).then(d => setTopics(d.topics || [])),
      fetch(`${API}/warroom/history`, { credentials: "include" }).then(r => r.json()).then(d => setHistory(d.briefings || [])).catch(() => {}),
      fetchIntelligence(),
    ]).then(() => setLoading(false)).catch(() => setLoading(false));
  }, [fetchIntelligence]);

  const generateBriefing = async (topicId) => {
    setSelectedTopic(topicId);
    setBriefing(null);
    setMode("briefing");
    setGenerating(true);
    try {
      const res = await fetch(`${API}/warroom/briefing`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ topic_id: topicId }),
      });
      if (res.ok) setBriefing(await res.json());
      else setBriefing({ error: true, briefing: "Failed to generate briefing." });
    } catch { setBriefing({ error: true, briefing: "Network error." }); }
    setGenerating(false);
  };

  const loadSavedBriefing = async (briefingId) => {
    try {
      const res = await fetch(`${API}/warroom/briefing/${briefingId}`, { credentials: "include" });
      if (res.ok) { const data = await res.json(); setBriefing(data); setSelectedTopic(data.topic_id); setMode("briefing"); }
    } catch {}
  };

  const submitFeedback = async (targetId, rating) => {
    try {
      await fetch(`${API}/warroom/feedback`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ target_id: targetId, rating }),
      });
    } catch {}
  };

  if (loading) return <div className="min-h-screen bg-[#0A1628] flex items-center justify-center"><Loader2 className="w-8 h-8 text-[#1A6BCC] animate-spin" /></div>;

  /* ═══ BRIEFING VIEW ═══ */
  if (mode === "briefing" && (generating || briefing)) {
    const topic = topics.find(t => t.id === selectedTopic);
    return (
      <div className="min-h-screen bg-[#0A1628] text-white" data-testid="warroom-briefing">
        <div className="sticky top-0 z-10 bg-[#0A1628]/95 backdrop-blur border-b border-[#1E3A5F]/50 px-6 py-3 flex items-center justify-between">
          <button onClick={() => setMode("intelligence")} className="flex items-center gap-2 text-sm text-slate-400 hover:text-white" data-testid="back-to-intel">
            <ArrowLeft className="w-4 h-4" /> Back to Intelligence
          </button>
        </div>
        <div className="max-w-3xl mx-auto px-6 py-8">
          <h1 className="text-2xl font-bold mb-4">{topic?.label || "Audit Briefing"}</h1>
          {generating ? (
            <div className="flex flex-col items-center py-20 gap-4"><Loader2 className="w-8 h-8 text-[#1A6BCC] animate-spin" /><div className="text-sm text-slate-400">Generating audit intelligence (assistive only)...</div></div>
          ) : briefing ? (
            <div className="bg-[#0D1F38] border border-[#1E3A5F]/50 rounded-lg p-6" data-testid="briefing-content">
              <div className="text-[10px] text-amber-400 uppercase mb-4">Assistive Intelligence — Review Required Before Use</div>
              <BriefingRenderer text={briefing.briefing} />
              <div className="flex items-center gap-2 mt-4 pt-3 border-t border-[#1E3A5F]">
                <span className="text-xs text-slate-500">Was this helpful?</span>
                <button onClick={() => submitFeedback(briefing.briefing_id, "useful")} className="p-1 text-slate-400 hover:text-emerald-400"><ThumbsUp className="w-4 h-4" /></button>
                <button onClick={() => submitFeedback(briefing.briefing_id, "not_useful")} className="p-1 text-slate-400 hover:text-red-400"><ThumbsDown className="w-4 h-4" /></button>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  /* ═══ INTELLIGENCE DASHBOARD ═══ */
  return (
    <div className="min-h-screen bg-[#0A1628] text-white" data-testid="warroom-page">
      <div className="border-b border-[#1E3A5F]/50 px-6 py-3">
        <div className="max-w-7xl mx-auto flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-red-600 rounded flex items-center justify-center"><Siren className="w-4 h-4 text-white" /></div>
            <div>
              <h1 className="font-bold text-sm">{t('warroom.title')}</h1>
              <p className="text-[10px] text-slate-500">{t('warroom.subtitle')}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setMode("intelligence")} data-testid="mode-intel"
              className={`px-3 py-1.5 rounded text-xs font-medium ${mode === "intelligence" ? "bg-red-600 text-white" : "bg-[#132340] text-[#94A3B8]"}`}>
              <Activity className="w-3 h-3 inline mr-1" /> {t('warroom.intelligence')}
            </button>
            <button onClick={() => setMode("topics")} data-testid="mode-topics"
              className={`px-3 py-1.5 rounded text-xs font-medium ${mode === "topics" ? "bg-red-600 text-white" : "bg-[#132340] text-[#94A3B8]"}`}>
              <Target className="w-3 h-3 inline mr-1" /> {t('warroom.briefings')}
            </button>
            <button onClick={fetchIntelligence} className="px-2 py-1.5 rounded bg-[#132340] text-[#94A3B8] hover:text-white text-xs">
              <Loader2 className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>

      {mode === "intelligence" && intel && (
        <div className="max-w-7xl mx-auto px-6 py-4 space-y-4">
          {/* KPI Cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="kpi-cards">
            {[
              { label: t('warroom.activeAudits'), value: intel.kpis.active_audits, color: "text-emerald-400" },
              { label: t('warroom.totalAudits'), value: intel.kpis.total_audits, color: "text-blue-400" },
              { label: t('warroom.totalFindings'), value: intel.kpis.total_findings, color: "text-amber-400" },
              { label: t('warroom.totalEvidence'), value: intel.kpis.total_evidence, color: "text-cyan-400" },
              { label: t('warroom.checklistPct'), value: `${intel.kpis.overall_checklist_pct}%`, color: "text-purple-400" },
              { label: t('warroom.backRoomQueue'), value: intel.kpis.backroom_queue_depth, color: intel.kpis.backroom_queue_depth > 5 ? "text-red-400" : "text-emerald-400" },
            ].map((kpi, i) => (
              <div key={i} className="bg-[#0D1F38] border border-[#1E3A5F] rounded-lg p-3">
                <span className="text-[10px] text-slate-500 uppercase tracking-wider">{kpi.label}</span>
                <div className={`text-2xl font-bold ${kpi.color}`}>{kpi.value}</div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Findings by Severity */}
            <div className="bg-[#0D1F38] border border-[#1E3A5F] rounded-lg p-4" data-testid="findings-by-severity">
              <h3 className="text-xs font-bold text-[#94A3B8] uppercase tracking-wider mb-3 flex items-center gap-2"><AlertTriangle className="w-3.5 h-3.5 text-amber-400" /> {t('warroom.findingsByType')}</h3>
              {Object.keys(intel.findings_by_severity).length === 0 ? <p className="text-xs text-slate-500">{t('warroom.noFindings')}</p> : (
                <div className="space-y-2">
                  {Object.entries(intel.findings_by_severity).sort((a, b) => b[1] - a[1]).map(([type, count]) => (
                    <div key={type} className="flex items-center justify-between">
                      <span className={`text-xs capitalize ${FINDING_COLORS[type] || "text-slate-400"}`}>{type.replace(/_/g, " ")}</span>
                      <span className="text-sm font-bold text-white">{count}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Posture Distribution */}
            <div className="bg-[#0D1F38] border border-[#1E3A5F] rounded-lg p-4" data-testid="posture-distribution">
              <h3 className="text-xs font-bold text-[#94A3B8] uppercase tracking-wider mb-3 flex items-center gap-2"><Eye className="w-3.5 h-3.5 text-cyan-400" /> {t('warroom.postureDistribution')}</h3>
              {Object.keys(intel.posture_distribution).length === 0 ? <p className="text-xs text-slate-500">{t('warroom.noPostureData')}</p> : (
                <div className="space-y-2">
                  {Object.entries(intel.posture_distribution).map(([posture, count]) => {
                    const total = Object.values(intel.posture_distribution).reduce((a, b) => a + b, 0);
                    return (
                      <div key={posture}>
                        <div className="flex items-center justify-between mb-0.5">
                          <span className={`text-xs capitalize ${POSTURE_COLORS[posture] || "text-slate-400"}`}>{posture.replace(/_/g, " ")}</span>
                          <span className="text-xs text-[#94A3B8]">{count} ({Math.round(count / total * 100)}%)</span>
                        </div>
                        <div className="w-full h-1.5 bg-[#132340] rounded-full">
                          <div className="h-full rounded-full bg-current" style={{ width: `${Math.round(count / total * 100)}%`, color: POSTURE_COLORS[posture]?.replace("text-", "") || "#94A3B8" }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Prevention Alerts + Recurrence */}
            <div className="bg-[#0D1F38] border border-[#1E3A5F] rounded-lg p-4" data-testid="recent-alerts">
              <h3 className="text-xs font-bold text-[#94A3B8] uppercase tracking-wider mb-3 flex items-center gap-2"><Zap className="w-3.5 h-3.5 text-amber-400" /> {t('warroom.alertsRecurrence')}</h3>
              {(intel.recent_alerts?.length || 0) + (intel.recurrence_alerts?.length || 0) === 0 ? (
                <p className="text-xs text-slate-500">{t('warroom.noAlerts')}</p>
              ) : (
                <div className="space-y-2">
                  {(intel.recurrence_alerts || []).slice(0, 3).map((sig, i) => (
                    <div key={i} className="p-2 rounded border border-purple-500/20 bg-purple-500/5">
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <Search className="w-3 h-3 text-purple-400" />
                        <span className="text-[10px] font-bold text-white">{sig.signal_type?.replace(/_/g, " ")}</span>
                      </div>
                      <span className="text-[10px] text-slate-400">{sig.explanation}</span>
                    </div>
                  ))}
                  {(intel.recent_alerts || []).slice(0, 3).map((alert, i) => (
                    <div key={`a-${i}`} className="p-2 rounded border border-[#1E3A5F]">
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <AlertTriangle className="w-3 h-3 text-amber-400" />
                        <span className="text-[10px] font-bold text-white">{alert.rule_name}</span>
                      </div>
                      <span className="text-[10px] text-slate-400">{alert.metric} = {alert.metric_value}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Exposed Clauses */}
            <div className="bg-[#0D1F38] border border-[#1E3A5F] rounded-lg p-4" data-testid="exposed-clauses">
              <h3 className="text-xs font-bold text-[#94A3B8] uppercase tracking-wider mb-3 flex items-center gap-2"><Shield className="w-3.5 h-3.5 text-red-400" /> {t('warroom.exposedClauses')}</h3>
              {(intel.exposed_clauses || []).length === 0 ? <p className="text-xs text-slate-500">{t('warroom.noExposedClauses')}</p> : (
                <div className="space-y-1.5">
                  {intel.exposed_clauses.map((c, i) => (
                    <div key={i} className="flex items-center justify-between p-2 bg-[#132340] rounded">
                      <div>
                        <span className="text-xs text-white font-medium">Cl. {c.clause}</span>
                        <span className="text-[10px] text-slate-500 ml-2">{c.audit_count} audit(s)</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`text-xs font-bold ${c.risk === "high" ? "text-red-400" : "text-amber-400"}`}>{c.finding_count} findings</span>
                        {c.major_critical_count > 0 && <span className="text-[10px] text-red-400">({c.major_critical_count} major/critical)</span>}
                        <button onClick={() => navigate("/defense-scripts")} className="text-[#94A3B8] hover:text-white" title="Open defense script"><Shield className="w-3 h-3" /></button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Evidence Gaps */}
            <div className="bg-[#0D1F38] border border-[#1E3A5F] rounded-lg p-4" data-testid="evidence-gaps">
              <h3 className="text-xs font-bold text-[#94A3B8] uppercase tracking-wider mb-3 flex items-center gap-2"><FileText className="w-3.5 h-3.5 text-amber-400" /> {t('warroom.evidenceGaps')}</h3>
              {(intel.evidence_gaps || []).length === 0 ? <p className="text-xs text-slate-500">{t('warroom.noEvidenceGaps')}</p> : (
                <div className="space-y-1.5 max-h-48 overflow-y-auto">
                  {intel.evidence_gaps.map((g, i) => (
                    <div key={i} className="flex items-center justify-between p-2 bg-[#132340] rounded">
                      <div className="flex-1 min-w-0">
                        <span className="text-xs text-white truncate block">{g.description || g.clause || g.checklist_item_id}</span>
                        <span className="text-[10px] text-slate-500">{g.standard} · {g.audit_id}</span>
                      </div>
                      <button onClick={() => navigate("/audit-conductor")} className="text-[#94A3B8] hover:text-white ml-2" title="Open checklist"><ListChecks className="w-3 h-3" /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Matched Defense Scripts */}
            <div className="bg-[#0D1F38] border border-[#1E3A5F] rounded-lg p-4" data-testid="defense-scripts-section">
              <h3 className="text-xs font-bold text-[#94A3B8] uppercase tracking-wider mb-3 flex items-center gap-2"><Shield className="w-3.5 h-3.5 text-emerald-400" /> {t('warroom.relevantDefenseScripts')}</h3>
              {(intel.defense_scripts || []).length === 0 ? <p className="text-xs text-slate-500">{t('warroom.noMatchedScripts')}</p> : (
                <div className="space-y-1.5">
                  {intel.defense_scripts.map((ds, i) => (
                    <button key={i} onClick={() => navigate("/defense-scripts")} className="w-full text-left p-2 bg-[#132340] rounded hover:bg-[#1E3A5F]/50">
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-mono text-[#94A3B8]">{ds.id}</span>
                        <span className="text-xs text-white">{ds.title}</span>
                      </div>
                      <span className="text-[10px] text-slate-500">{ds.standard}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Overnight Priorities */}
            <div className="bg-[#0D1F38] border border-[#1E3A5F] rounded-lg p-4" data-testid="overnight-priorities">
              <h3 className="text-xs font-bold text-[#94A3B8] uppercase tracking-wider mb-3 flex items-center gap-2"><Clock className="w-3.5 h-3.5 text-purple-400" /> {t('warroom.overnightPriorities')}</h3>
              {(intel.overnight_priorities || []).length === 0 ? <p className="text-xs text-slate-500">{t('warroom.noPriorities')}</p> : (
                <div className="space-y-1.5">
                  {intel.overnight_priorities.map((p, i) => (
                    <div key={i} className={`p-2 rounded border ${p.priority === "critical" ? "border-red-500/30 bg-red-500/5" : p.priority === "high" ? "border-orange-500/20 bg-orange-500/5" : "border-[#1E3A5F]"}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <span className={`text-[10px] font-bold uppercase ${RISK_COLORS[p.priority]}`}>{p.priority}</span>
                          <p className="text-xs text-white mt-0.5">{p.description}</p>
                        </div>
                        <QuickActionButton action={p.quick_action} navigate={navigate} auditId={p.audit_id} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Audit Comparison Table */}
          <div className="bg-[#0D1F38] border border-[#1E3A5F] rounded-lg p-4" data-testid="audit-comparison">
            <h3 className="text-xs font-bold text-[#94A3B8] uppercase tracking-wider mb-3 flex items-center gap-2"><BarChart3 className="w-3.5 h-3.5 text-blue-400" /> {t('warroom.activeAuditComparison')}</h3>
            {intel.audit_summaries.length === 0 ? <p className="text-xs text-slate-500">{t('warroom.noActiveAudits')}</p> : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-[#1E3A5F]">
                      <th className="text-left text-[#94A3B8] py-2 px-2">{t('warroom.thAudit')}</th>
                      <th className="text-center text-[#94A3B8] py-2 px-2">{t('warroom.thStatus')}</th>
                      <th className="text-center text-[#94A3B8] py-2 px-2">{t('warroom.thRisk')}</th>
                      <th className="text-center text-[#94A3B8] py-2 px-2">{t('warroom.thChecklist')}</th>
                      <th className="text-center text-[#94A3B8] py-2 px-2">{t('warroom.thFindings')}</th>
                      <th className="text-center text-[#94A3B8] py-2 px-2">{t('warroom.thBackRoom')}</th>
                      <th className="text-center text-[#94A3B8] py-2 px-2">{t('warroom.thActions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {intel.audit_summaries.map(a => (
                      <tr key={a.audit_id} className="border-b border-[#1E3A5F]/50 hover:bg-[#132340]/50">
                        <td className="py-2 px-2">
                          <div className="text-white font-medium truncate max-w-[180px]">{a.title}</div>
                          <div className="text-[10px] text-[#94A3B8] font-mono">{a.audit_id}</div>
                        </td>
                        <td className="text-center py-2 px-2">
                          <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-blue-500/20 text-blue-400 capitalize">{a.status.replace(/_/g, " ")}</span>
                        </td>
                        <td className="text-center py-2 px-2"><span className={`font-bold uppercase ${RISK_COLORS[a.risk_level]}`}>{a.risk_level}</span></td>
                        <td className="text-center py-2 px-2">
                          <span className={`font-bold ${a.checklist_pct >= 80 ? "text-emerald-400" : a.checklist_pct >= 40 ? "text-amber-400" : "text-red-400"}`}>{a.checklist_pct}%</span>
                        </td>
                        <td className="text-center py-2 px-2"><span className={`font-bold ${a.findings_count > 0 ? "text-amber-400" : "text-emerald-400"}`}>{a.findings_count}</span></td>
                        <td className="text-center py-2 px-2"><span className={`font-bold ${a.backroom_pending > 5 ? "text-red-400" : "text-emerald-400"}`}>{a.backroom_pending}</span></td>
                        <td className="text-center py-2 px-2">
                          <div className="flex items-center justify-center gap-1">
                            <button onClick={() => navigate("/audit-conductor")} className="p-1 text-[#94A3B8] hover:text-cyan-400" title="Open Audit"><ExternalLink className="w-3 h-3" /></button>
                            <button onClick={() => navigate("/scriber")} className="p-1 text-[#94A3B8] hover:text-emerald-400" title="Launch SCRIBER"><FileText className="w-3 h-3" /></button>
                            <button onClick={() => navigate("/defense-scripts")} className="p-1 text-[#94A3B8] hover:text-purple-400" title="Defense Scripts"><Shield className="w-3 h-3" /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* TOPICS MODE */}
      {mode === "topics" && (
        <div className="max-w-4xl mx-auto px-6 py-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-6" data-testid="topics-grid">
            {topics.map(topic => (
              <button key={topic.id} onClick={() => generateBriefing(topic.id)}
                className="text-left bg-[#0D1F38] border border-[#1E3A5F] rounded-lg p-3 hover:border-[#1A6BCC]/50 transition-colors group"
                data-testid={`topic-${topic.id}`}>
                <div className="text-[10px] uppercase tracking-wider font-bold text-cyan-400 mb-1">{topic.category?.replace("_", " ")}</div>
                <h3 className="text-sm font-bold text-white group-hover:text-[#00C8E0] transition-colors">{topic.label}</h3>
              </button>
            ))}
          </div>
          {history.length > 0 && (
            <div>
              <h2 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2 flex items-center gap-2"><Clock className="w-3.5 h-3.5" /> {t('warroom.recentBriefings')}</h2>
              <div className="space-y-1.5" data-testid="briefing-history">
                {history.slice(0, 10).map(h => (
                  <button key={h.briefing_id} onClick={() => loadSavedBriefing(h.briefing_id)}
                    className="w-full text-left bg-[#0D1F38] border border-[#1E3A5F] rounded-lg p-2.5 hover:border-[#1A6BCC]/40 flex items-center gap-3">
                    <Siren className="w-4 h-4 text-slate-500 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-bold text-white truncate">{h.topic_label}</div>
                      <div className="text-[10px] text-slate-500">{new Date(h.created_at).toLocaleDateString()}</div>
                    </div>
                    <ChevronRight className="w-3.5 h-3.5 text-slate-600 shrink-0" />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* Quick Action Buttons */
const QuickActionButton = ({ action, navigate, auditId }) => {
  const actions = {
    open_related_finding: { icon: AlertTriangle, label: "Finding", path: "/audit-conductor" },
    create_backroom_task: { icon: MessageSquare, label: "Back Room", path: "/audit-conductor" },
    open_defense_script: { icon: Shield, label: "Defense", path: "/defense-scripts" },
    open_related_checklist: { icon: ListChecks, label: "Checklist", path: "/audit-conductor" },
    launch_scriber: { icon: FileText, label: "SCRIBER", path: "/scriber" },
  };
  const a = actions[action];
  if (!a) return null;
  return (
    <button onClick={() => navigate(a.path)} data-testid={`quick-action-${action}`}
      className="flex items-center gap-1 px-2 py-1 bg-[#132340] text-[#94A3B8] hover:text-white rounded text-[10px] shrink-0">
      <a.icon className="w-3 h-3" /> {a.label}
    </button>
  );
};

/* Briefing Renderer */
const BriefingRenderer = ({ text }) => {
  if (!text) return null;
  return <div>{text.split("\n").map((line, i) => {
    if (line.startsWith("## ")) return <h2 key={i} className="text-sm font-bold text-white mt-6 mb-3 border-b border-[#1E3A5F]/50 pb-2">{line.replace("## ", "")}</h2>;
    if (line.startsWith("# ")) return <h1 key={i} className="text-lg font-bold text-white mt-4 mb-2">{line.replace("# ", "")}</h1>;
    if (line.startsWith("- ") || line.startsWith("* ")) return <div key={i} className="flex gap-2 mb-1.5 pl-2"><span className="text-[#00C8E0]">*</span><span className="text-sm text-slate-300">{line.replace(/^[-*]\s/, "")}</span></div>;
    if (line.trim()) return <p key={i} className="text-sm text-slate-300 mb-2">{line}</p>;
    return null;
  })}</div>;
};

export default WarRoomPage;
